import { mixed, type AnySchema } from 'yup';
import { type ImageGallerySettings } from './contracts';
import {
    assertNoUnknownKeys,
    parseRuntimeBindingFromSettingsByValueKind,
} from '../runtimeBindingSettingsYupUtils';
import { parseContainCoverFit } from '../image/fitUtils';
import { isRecord } from '../../../../../utils/typeGuards';

const IMAGE_GALLERY_ROOT_ALLOWED_KEYS = ['binding', 'imagesPerPage', 'fit'] as const;

function parseImagesPerPage(value: unknown): 2 | 4 {
    if (typeof value === 'undefined' || value === null || value === '') {
        throw new Error('Expected "image_gallery.imagesPerPage" to be 2 or 4.');
    }

    const normalizedValue =
        typeof value === 'string' ? Number.parseInt(value, 10) : (value as number);
    if (normalizedValue !== 2 && normalizedValue !== 4) {
        throw new Error('Expected "image_gallery.imagesPerPage" to be 2 or 4.');
    }

    return normalizedValue;
}

export function normalizeImageGallerySettings(rawSettings: unknown): ImageGallerySettings {
    if (!isRecord(rawSettings)) {
        throw new Error('Expected "image_gallery" settings to be an object.');
    }

    assertNoUnknownKeys(rawSettings, IMAGE_GALLERY_ROOT_ALLOWED_KEYS, 'image_gallery');
    return {
        binding: parseRuntimeBindingFromSettingsByValueKind({
            settings: rawSettings,
            settingsPath: 'image_gallery',
            valueKind: 'image-list',
        }),
        imagesPerPage: parseImagesPerPage(rawSettings.imagesPerPage),
        fit: parseContainCoverFit(rawSettings.fit, 'image_gallery.fit'),
    };
}

export function buildImageGallerySettingsYupSchema(): AnySchema {
    return mixed()
        .required('Expected "image_gallery" settings to be an object.')
        .test(
            'image-gallery-settings-shape',
            'Invalid image gallery settings.',
            function test(value) {
                if (!isRecord(value)) {
                    return this.createError({
                        message: 'Expected "image_gallery" settings to be an object.',
                    });
                }

                try {
                    normalizeImageGallerySettings(value);
                    return true;
                } catch (error) {
                    const normalizedError =
                        error instanceof Error ? error : new Error(String(error));
                    return this.createError({ message: normalizedError.message });
                }
            }
        );
}
